import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

const QuoteRequestModal = ({ product, isOpen, onClose }) => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleClose = () => {
        onClose();
        // reset shortly after close animation finishes
        setTimeout(() => {
            setStatus("idle");
            setForm({ name: "", email: "", phone: "", message: "" });
            setErrorMsg("");
        }, 300);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus("submitting");
        setErrorMsg("");
        try {
            await axios.post(`${API}/api/store/quote-requests`, {
                productId: product._id,
                ...form,
            });
            setStatus("success");
        } catch (err) {
            setStatus("error");
            setErrorMsg(err.response?.data?.message || "Something went wrong. Please try again.");
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    >
                        <div className="w-full max-w-md bg-neutral-950 border border-neutral-800 rounded-3xl p-8 relative">
                            <button
                                onClick={handleClose}
                                className="absolute top-5 right-5 text-neutral-500 hover:text-white transition-colors"
                                aria-label="Close"
                            >
                                ✕
                            </button>

                            {status === "success" ? (
                                <div className="text-center py-8">
                                    <div className="text-5xl mb-4">✓</div>
                                    <h3 className="text-2xl font-bold text-white mb-2">Request Sent</h3>
                                    <p className="text-neutral-400 mb-6">
                                        Thanks — our team will reach out about <span className="text-white">{product.name}</span> shortly.
                                    </p>
                                    <button
                                        onClick={handleClose}
                                        className="bg-white text-black font-bold px-6 py-3 rounded-full hover:bg-purple-100 transition-colors"
                                    >
                                        Done
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="text-xs font-bold text-purple-400 tracking-widest uppercase mb-2">Request a Quote</div>
                                    <h3 className="text-2xl font-bold text-white mb-1">{product.name}</h3>
                                    <p className="text-neutral-500 text-sm mb-6">Tell us a bit about you and we'll follow up with pricing.</p>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-400 mb-1">Name</label>
                                            <input
                                                name="name" required value={form.name} onChange={handleChange}
                                                className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-400 mb-1">Email</label>
                                            <input
                                                type="email" name="email" required value={form.email} onChange={handleChange}
                                                className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-400 mb-1">Phone</label>
                                            <input
                                                name="phone" required value={form.phone} onChange={handleChange}
                                                className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-400 mb-1">Message (optional)</label>
                                            <textarea
                                                name="message" value={form.message} onChange={handleChange} rows={3}
                                                className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            />
                                        </div>

                                        {status === "error" && (
                                            <p className="text-red-400 text-sm">{errorMsg}</p>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={status === "submitting"}
                                            className="w-full bg-white text-black font-bold py-3.5 rounded-full hover:bg-purple-100 disabled:opacity-60 transition-colors"
                                        >
                                            {status === "submitting" ? "Sending…" : "Submit Request"}
                                        </button>
                                    </form>
                                </>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default QuoteRequestModal;